import React, { useCallback, useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import "@/App.css";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import ScrollToTop from "@/components/ScrollToTop";

import Home from "@/pages/Home";
import Privacy from "@/pages/Privacy";
import CookiePolicy from "@/pages/CookiePolicy";
import Terms from "@/pages/Terms";
import ThankYou from "@/pages/ThankYou";
import NotFound from "@/pages/NotFound";

// Component that syncs i18n language to URL param and updates <html lang>
function LangSync() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  useEffect(() => {
    const target = lang === "en" ? "en" : "ar";
    if (i18n.language !== target) i18n.changeLanguage(target);
    document.documentElement.lang = target === "en" ? "en" : "es-AR";
    // Update document title/description for SEO on lang switch
    if (target === "en") {
      document.title = "Zantravora — Real estate crowdlending in Argentina";
    } else {
      document.title = "Zantravora — Crowdlending inmobiliario en Argentina";
    }
  }, [lang, i18n]);
  return null;
}

// Redirect root "/" to detected/default language
function RootRedirect() {
  // Default: Spanish (ar) for Argentina audience. Use stored preference if present.
  const stored = typeof window !== "undefined" ? localStorage.getItem("zv_lang") : null;
  const target = stored === "en" ? "en" : "ar";
  return <Navigate to={`/${target}`} replace />;
}

function Layout({ children, onOpenCookies }) {
  const location = useLocation();
  const hideChrome = location.pathname === "/thank_you";
  if (hideChrome) return children;
  return (
    <>
      <Header />
      {children}
      <Footer onOpenCookies={onOpenCookies} />
    </>
  );
}

function App() {
  const [cookieOpenSignal, setCookieOpenSignal] = useState(0);

  const openCookies = useCallback(() => setCookieOpenSignal((n) => n + 1), []);

  // Save language preference when it changes (post-mount)
  useEffect(() => {
    const path = window.location.pathname;
    const m = path.match(/^\/(ar|en)/);
    if (m) localStorage.setItem("zv_lang", m[1]);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Layout onOpenCookies={openCookies}><RootRedirect /></Layout>} />
          <Route path="/thank_you" element={<ThankYou />} />
          <Route
            path="/:lang"
            element={
              <Layout onOpenCookies={openCookies}>
                <LangSync />
                <Home />
              </Layout>
            }
          />
          <Route
            path="/:lang/privacy"
            element={
              <Layout onOpenCookies={openCookies}>
                <LangSync />
                <Privacy />
              </Layout>
            }
          />
          <Route
            path="/:lang/cookies"
            element={
              <Layout onOpenCookies={openCookies}>
                <LangSync />
                <CookiePolicy onOpenPrefs={openCookies} />
              </Layout>
            }
          />
          <Route
            path="/:lang/terms"
            element={
              <Layout onOpenCookies={openCookies}>
                <LangSync />
                <Terms />
              </Layout>
            }
          />
          <Route
            path="/:lang/*"
            element={
              <Layout onOpenCookies={openCookies}>
                <LangSync />
                <NotFound />
              </Layout>
            }
          />
          <Route
            path="*"
            element={
              <Layout onOpenCookies={openCookies}>
                <NotFound />
              </Layout>
            }
          />
        </Routes>
        <CookieBanner openSignal={cookieOpenSignal} />
      </BrowserRouter>
    </div>
  );
}

export default App;
